import {
    EMAIL_ATTACHMENT_ALLOWED_EXTENSIONS,
    EMAIL_ATTACHMENT_MAX_COUNT,
    EMAIL_ATTACHMENT_MAX_FILE_BYTES,
    EMAIL_ATTACHMENT_MAX_TOTAL_BYTES,
} from '../../constant';
import { downloadEmailAttachment } from '../../store/actions/auth';

const BYTES_PER_MB = 1024 * 1024;

export const formatFileSize = (bytes = 0) => {
    if (bytes >= BYTES_PER_MB) return `${(bytes / BYTES_PER_MB).toFixed(1).replace(/\.0$/, '')} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const getExtension = (name = '') => {
    const dot = name.lastIndexOf('.');
    return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
};

// Maps an attachment returned by the API (or an upload result) to the shape the UI keeps in state.
export const toAttachment = (item) => ({
    id: item.id || 0,
    fileName: item.fileName,
    fileSize: item.fileSize,
    filePath: item.filePath,
    fileType: item.fileType,
});

// Checks files against the attachment rules before upload. Files that pass are returned in `accepted`;
// each failing file gets an error naming it and the reason, and is not attached.
export const validateAttachmentFiles = (files, existing = []) => {
    const accepted = [];
    const errors = [];
    const names = new Set(existing.map((a) => a.fileName.toLowerCase()));
    let count = existing.length;
    let totalBytes = existing.reduce((sum, a) => sum + (a.fileSize || 0), 0);

    files.forEach((file) => {
        let reason = null;
        if (!EMAIL_ATTACHMENT_ALLOWED_EXTENSIONS.includes(getExtension(file.name))) {
            reason = 'Invalid file type';
        } else if (file.size === 0) {
            reason = 'File is empty';
        } else if (file.size > EMAIL_ATTACHMENT_MAX_FILE_BYTES) {
            reason = `File exceeds ${formatFileSize(EMAIL_ATTACHMENT_MAX_FILE_BYTES)}`;
        } else if (names.has(file.name.toLowerCase())) {
            reason = 'Duplicate file name';
        } else if (count >= EMAIL_ATTACHMENT_MAX_COUNT) {
            reason = `Maximum of ${EMAIL_ATTACHMENT_MAX_COUNT} attachments allowed`;
        } else if (totalBytes + file.size > EMAIL_ATTACHMENT_MAX_TOTAL_BYTES) {
            reason = `Total attachment size exceeds ${formatFileSize(EMAIL_ATTACHMENT_MAX_TOTAL_BYTES)}`;
        }

        if (reason) {
            errors.push({ fileName: file.name, reason });
            return;
        }
        accepted.push(file);
        names.add(file.name.toLowerCase());
        count += 1;
        totalBytes += file.size;
    });

    return { accepted, errors };
};

// Files a browser can show inline; other types (Word, Excel, CSV) can only be downloaded.
const VIEWABLE_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg']);

export const isViewable = (fileName = '') => VIEWABLE_EXTENSIONS.has(getExtension(fileName));

// Fetches the file through the API (so access stays authenticated) and either opens it in a new tab or saves it.
export const openAttachment = async (attachment, { download = false, templateId } = {}) => {
    // The tab is opened before the request so the browser does not treat it as a blocked popup.
    const viewTab = download ? null : window.open('', '_blank');
    try {
        const response = await downloadEmailAttachment(attachment.filePath, templateId);
        const blob = new Blob([response.data], { type: attachment.fileType || response.data?.type });
        const url = URL.createObjectURL(blob);
        if (viewTab) {
            viewTab.location.href = url;
        } else {
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        return true;
    } catch {
        if (viewTab) viewTab.close();
        return false;
    }
};

// Attachments are optional: an empty list simply sends nothing.
export const getAttachmentPayload = (attachments = []) => attachments.map(toAttachment);
